import type { AutomationActionType } from '../types/AutomationWorkflow.types';
import { defaultIfElseConfig } from './ifElseConfig';

export type AutomationBlockParamType =
  | 'string'
  | 'folder'
  | 'boolean'
  | 'select'
  | 'textarea'
  | 'tags';

export interface AutomationBlockParamDef {
  type: AutomationBlockParamType;
  label: string;
  description?: string;
  required?: boolean;
  default?: unknown;
  options?: { value: string; label: string }[];
  placeholder?: string;
  allowVariableRef?: boolean;
}

export interface AutomationBlockDefinition {
  type: AutomationActionType;
  title: string;
  category: string;
  description: string;
  inputParams: Record<string, AutomationBlockParamDef>;
  outputParams: Record<string, { type: string; description: string }>;
}

export const AUTOMATION_BLOCK_CATEGORIES = [
  'File Operations',
  'Library',
  'Logic',
  'Serato',
  'Tags',
  'Agent',
] as const;

export const AUTOMATION_BLOCK_CATALOG: AutomationBlockDefinition[] = [
  {
    type: 'copy_move_file',
    title: 'Copy / move file',
    category: 'File Operations',
    description: 'Copy or move files to a folder you choose.',
    inputParams: {
      mode: {
        type: 'select',
        label: 'Mode',
        required: true,
        default: 'copy',
        options: [
          { value: 'copy', label: 'Copy' },
          { value: 'move', label: 'Move' },
        ],
      },
      destinationFolder: {
        type: 'folder',
        label: 'Destination',
        required: true,
        allowVariableRef: true,
        placeholder: '~/Music/Library/Sorted/{genre}',
      },
      onConflict: {
        type: 'select',
        label: 'If it already exists',
        default: 'keep_both',
        options: [
          { value: 'keep_both', label: 'Keep both copies' },
          { value: 'overwrite', label: 'Overwrite' },
          { value: 'skip', label: 'Skip' },
        ],
      },
    },
    outputParams: {
      files: { type: 'list', description: 'Resulting file paths' },
    },
  },
  {
    type: 'add_to_serato_crate',
    title: 'Add to Serato crate',
    category: 'Serato',
    description: 'Export files into a Serato crate you name.',
    inputParams: {
      crateName: {
        type: 'string',
        label: 'Crate name',
        required: true,
      },
      parentPath: {
        type: 'string',
        label: 'Parent path',
        placeholder: 'Optional, comma-separated',
        allowVariableRef: true,
      },
    },
    outputParams: {
      files: { type: 'list', description: 'Track file paths added' },
    },
  },
  {
    type: 'read_write_tags',
    title: 'Read / write tags',
    category: 'Tags',
    description: 'Read or update ID3 tags on incoming files.',
    inputParams: {
      mode: {
        type: 'select',
        label: 'Mode',
        required: true,
        default: 'read',
        options: [
          { value: 'read', label: 'Read tags' },
          { value: 'write', label: 'Write tags' },
        ],
      },
      title: { type: 'string', label: 'Title', allowVariableRef: true },
      artist: { type: 'string', label: 'Artist', allowVariableRef: true },
      album: { type: 'string', label: 'Album', allowVariableRef: true },
      genre: { type: 'string', label: 'Genre', allowVariableRef: true },
      grouping: {
        type: 'string',
        label: 'Grouping',
        allowVariableRef: true,
      },
      comment: {
        type: 'string',
        label: 'Comment',
        allowVariableRef: true,
        placeholder: 'sourced {date}',
      },
    },
    outputParams: {
      files: { type: 'list', description: 'Processed file paths' },
      tags: { type: 'object', description: 'Tags read from the last file' },
    },
  },
  {
    type: 'import_to_library',
    title: 'Import to library',
    category: 'Library',
    description: 'Index the file in My Library.',
    inputParams: {
      analyzeBpmKey: {
        type: 'boolean',
        label: 'Analyze BPM & key',
        default: true,
      },
      flagDuplicates: {
        type: 'boolean',
        label: 'Flag duplicates',
        default: true,
      },
    },
    outputParams: {
      files: { type: 'list', description: 'Imported file paths' },
      tags: { type: 'object', description: 'Tags from the imported track' },
      flaggedDuplicate: {
        type: 'boolean',
        description: 'Whether the file matched an existing duplicate group',
      },
    },
  },
  {
    type: 'if_else',
    title: 'If / else',
    category: 'Logic',
    description:
      'Split on BPM and run a different nested action in each branch.',
    inputParams: {
      minBpm: {
        type: 'string',
        label: 'Minimum BPM',
        default: '120',
      },
      maxBpm: {
        type: 'string',
        label: 'Maximum BPM',
        default: '128',
      },
    },
    outputParams: {
      branchTaken: { type: 'string', description: 'true or false branch' },
    },
  },
  {
    type: 'run_agent',
    title: 'Run Ask Crate Hackers',
    category: 'Agent',
    description: 'Send a saved prompt through the chat agent.',
    inputParams: {
      instructions: {
        type: 'textarea',
        label: 'Instructions',
        required: true,
      },
    },
    outputParams: {},
  },
];

export const getBlockDefinition = (
  type: AutomationActionType,
): AutomationBlockDefinition | undefined =>
  AUTOMATION_BLOCK_CATALOG.find((block) => block.type === type);

export const getBlocksByCategory = (): Record<
  string,
  AutomationBlockDefinition[]
> => {
  const grouped: Record<string, AutomationBlockDefinition[]> = {};
  AUTOMATION_BLOCK_CATALOG.forEach((block) => {
    if (!grouped[block.category]) grouped[block.category] = [];
    grouped[block.category].push(block);
  });
  return grouped;
};

export const defaultBlockConfig = (
  type: AutomationActionType,
): Record<string, unknown> => {
  if (type === 'if_else') return defaultIfElseConfig();
  const definition = getBlockDefinition(type);
  if (!definition) return {};
  const config: Record<string, unknown> = {};
  Object.entries(definition.inputParams).forEach(([key, param]) => {
    if (param.default !== undefined) config[key] = param.default;
    else if (param.type === 'boolean') config[key] = false;
    else if (param.type === 'select' && param.options?.length)
      config[key] = param.options[0].value;
    else config[key] = '';
  });
  return config;
};
